/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { BadgeCheck, Copy, Download, FileJson, Fingerprint, Loader2, RefreshCw, ShieldAlert, ShieldCheck, Stamp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import api, { describeError } from '@/lib/api';
import { useAsyncAction } from '@/hooks/useAsyncAction';
import { SealedDownloadButton, TimestampButton } from '@/components/legal/LegalProofTools';
import { fmt, short, copy, downloadBlob } from '@/components/legal/proof/helpers';

export const ChainStatusBadge = ({ chain }) => {
  if (chain?.ok) {
    return (
      <Badge className="rounded-full bg-primary/10 text-primary hover:bg-primary/10">
        <ShieldCheck className="h-3 w-3 mr-1" /> Integra · {chain.checked} eventi
      </Badge>
    );
  }
  return (
    <Badge variant="destructive" className="rounded-full">
      <ShieldAlert className="h-3 w-3 mr-1" /> {chain ? `${chain.broken_count} anomalie` : 'non verificata'}
    </Badge>
  );
};

/** Corpo tabella accettazioni: errore → retry, loading → skeleton, vuoto → messaggio, altrimenti tabella. */
export const AcceptancesTableBody = ({ error, loading, data, onRetry, onCopy }) => {
  if (error) {
    return (
      <CardContent className="p-6 text-sm text-destructive flex items-center justify-between gap-3" data-testid="eula-acceptances-error">
        <span>{error}</span><Button variant="outline" size="sm" onClick={onRetry}>Riprova</Button>
      </CardContent>
    );
  }
  if (loading) {
    return <CardContent className="p-4 space-y-2">{['s0', 's1', 's2'].map((k) => <Skeleton key={k} className="h-9 w-full" />)}</CardContent>;
  }
  if (!data?.items?.length) {
    return (
      <CardContent className="p-8 text-center text-sm text-muted-foreground" data-testid="eula-acceptances-empty">
        Nessuna accettazione registrata finora. Il primo accesso di un utente demo creerà la prima voce.
      </CardContent>
    );
  }
  return (
    <div className="overflow-x-auto">
      <Table data-testid="eula-acceptances-table">
        <TableHeader>
          <TableRow>
            <TableHead className="w-10">#</TableHead>
            <TableHead>Data/ora</TableHead>
            <TableHead>Utente</TableHead>
            <TableHead>Ruolo</TableHead>
            <TableHead>Ver.</TableHead>
            <TableHead>IP</TableHead>
            <TableHead>Hash evento</TableHead>
            <TableHead>Stato</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.items.map((it) => (
            <TableRow key={it.audit_id} data-testid={`eula-acceptance-row-${it.seq}`}>
              <TableCell className="text-xs text-muted-foreground">{it.seq}</TableCell>
              <TableCell className="text-xs whitespace-nowrap">{fmt(it.accepted_at)}</TableCell>
              <TableCell className="text-xs"><div className="font-medium">{it.email || it.user_id}</div>{it.name && <div className="text-muted-foreground">{it.name}</div>}</TableCell>
              <TableCell className="text-xs">{it.role || '—'}</TableCell>
              <TableCell className="text-xs">{it.version}</TableCell>
              <TableCell className="text-xs font-mono">{it.ip || '—'}</TableCell>
              <TableCell className="text-xs"><button type="button" className="font-mono hover:text-primary inline-flex items-center gap-1" onClick={() => onCopy(it.hash, 'Hash copiato')} title={it.hash}>{short(it.hash)} <Copy className="h-3 w-3" aria-hidden="true" /></button></TableCell>
              <TableCell className="text-xs">{it.still_valid ? <Badge className="rounded-full bg-primary/10 text-primary hover:bg-primary/10">valida</Badge> : <Badge variant="outline" className="rounded-full">superata</Badge>}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export const AcceptancesRegister = ({ onExported }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastExport, setLastExport] = useState(null);
  const { run: runPdf, busy: busyPdf } = useAsyncAction();
  const { run: runJson, busy: busyJson } = useAsyncAction();
  const { run: runSealed, busy: busySealed } = useAsyncAction();

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { const r = await api.get('/legal/eula/acceptances', { params: { limit: 500 } }); setData(r.data); }
    catch (e) { setError(describeError(e, 'Impossibile caricare il registro')); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const exportPdf = () => runPdf(async () => {
    const r = await api.get('/legal/eula/acceptances/export.pdf', { responseType: 'blob' });
    const digest = r.headers['x-document-sha256'] || '';
    const cd = r.headers['content-disposition'] || '';
    const m = /filename="?([^";]+)"?/.exec(cd);
    const filename = m ? m[1] : `sanitanexus-prova-legale-eula-${digest.slice(0, 12)}.pdf`;
    downloadBlob(r.data, filename);
    if (digest) {
      // sidecar .sha256 (formato `sha256sum`) pronto per la marcatura eIDAS / ancoraggio blockchain
      downloadBlob(new Blob([`${digest}  ${filename}\n`], { type: 'text/plain' }), `${filename}.sha256`);
    }
    setLastExport({ format: 'PDF', filename, digest, dataset: r.headers['x-dataset-sha256'] || '', head: r.headers['x-audit-head-hash'] || '', at: new Date().toISOString() });
    onExported?.();
    return digest;
  }, { success: (d) => `PDF generato · SHA-256 ${short(d)}` });

  const exportSealed = () => runSealed(async () => {
    const r = await api.post('/legal/eula/acceptances/export-sealed.pdf', null, { responseType: 'blob' });
    const digest = r.headers['x-document-sha256'] || '';
    const original = r.headers['x-original-sha256'] || '';
    const cd = r.headers['content-disposition'] || '';
    const m = /filename="?([^";]+)"?/.exec(cd);
    const filename = m ? m[1] : `sanitanexus-prova-legale-eula-${original.slice(0, 12)}-sigillato.pdf`;
    downloadBlob(r.data, filename);
    if (original) downloadBlob(new Blob([`${original}  ${filename.replace('-sigillato', '')}\n`], { type: 'text/plain' }), `${filename.replace('-sigillato', '')}.sha256`);
    setLastExport({
      format: 'PDF sigillato', filename, digest, original, dataset: r.headers['x-dataset-sha256'] || '', head: r.headers['x-audit-head-hash'] || '', at: new Date().toISOString(),
      timestamp: { gen_time: r.headers['x-timestamp-gentime'], serial_number: r.headers['x-timestamp-serial'], signer_cn: r.headers['x-timestamp-tsa'], sealed: true },
    });
    onExported?.();
    return r.headers['x-timestamp-gentime'];
  }, { success: (g) => `PDF sigillato con marca temporale del ${fmt(g)}` });

  const exportJson = () => runJson(async () => {
    const r = await api.get('/legal/eula/acceptances/export.json', { responseType: 'blob' });
    const digest = r.headers['x-document-sha256'] || '';
    const filename = `sanitanexus-eula-acceptances-${digest.slice(0, 12)}.json`;
    downloadBlob(r.data, filename);
    setLastExport({ format: 'JSON', filename, digest, dataset: '', head: '', at: new Date().toISOString() });
    onExported?.();
    return digest;
  }, { success: (d) => `JSON esportato · SHA-256 ${short(d)}` });

  const chain = data?.chain;

  return (
    <div className="not-prose space-y-4" data-testid="eula-acceptances-admin">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-semibold tracking-tight flex items-center gap-2">
            <Fingerprint className="h-5 w-5 text-primary" aria-hidden="true" /> Registro accettazioni (solo amministratore)
          </h2>
          <p className="text-sm text-muted-foreground mt-1">Ogni accettazione è un evento immutabile della catena audit SHA-256: utente, data/ora, IP, hash e hash precedente.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="rounded-xl h-10" onClick={load} disabled={loading} data-testid="eula-acceptances-reload">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" /> Aggiorna
          </Button>
          <Button variant="outline" className="rounded-xl h-10" onClick={exportJson} disabled={busyJson || loading} data-testid="eula-export-json-btn">
            {busyJson ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <FileJson className="h-4 w-4" aria-hidden="true" />} Esporta JSON
          </Button>
          <Button variant="outline" className="rounded-xl h-10" onClick={exportPdf} disabled={busyPdf || busySealed || loading} data-testid="eula-export-pdf-btn">
            {busyPdf ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Download className="h-4 w-4" aria-hidden="true" />} PDF prova legale
          </Button>
          <Button className="rounded-xl h-10" onClick={exportSealed} disabled={busySealed || busyPdf || loading} title="Genera il PDF, ottiene la marca temporale RFC 3161 e aggiunge la pagina di sigillo con originale e ricevuta .tsr allegati" data-testid="eula-export-sealed-btn">
            {busySealed ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Stamp className="h-4 w-4" aria-hidden="true" />} PDF sigillato con marca
          </Button>
        </div>
      </div>

      {/* Stato catena */}
      <div className="grid sm:grid-cols-3 gap-3">
        <Card className="rounded-2xl"><CardContent className="p-4">
          <div className="text-xs text-muted-foreground">Accettazioni registrate</div>
          <div className="font-display text-2xl font-semibold" data-testid="eula-acceptances-count">{loading ? <Skeleton className="h-7 w-12" /> : (data?.count ?? 0)}</div>
        </CardContent></Card>
        <Card className="rounded-2xl"><CardContent className="p-4">
          <div className="text-xs text-muted-foreground">Integrità catena audit</div>
          <div className="flex items-center gap-2 mt-1" data-testid="eula-chain-status">
            {loading ? <Skeleton className="h-6 w-24" /> : <ChainStatusBadge chain={chain} />}
          </div>
        </CardContent></Card>
        <Card className="rounded-2xl"><CardContent className="p-4">
          <div className="text-xs text-muted-foreground">Head hash catena</div>
          <button type="button" onClick={() => copy(chain?.head_hash || '', 'Head hash copiato')} className="font-mono text-xs mt-1 text-left hover:text-primary inline-flex items-center gap-1" data-testid="eula-chain-head" disabled={!chain?.head_hash}>
            {loading ? <Skeleton className="h-4 w-32" /> : <>{short(chain?.head_hash)} <Copy className="h-3 w-3" aria-hidden="true" /></>}
          </button>
        </CardContent></Card>
      </div>

      {lastExport && (
        <Card className="rounded-2xl border-primary/30 bg-primary/5" data-testid="eula-last-export">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><BadgeCheck className="h-4 w-4 text-primary" aria-hidden="true" /> Export {lastExport.format} pronto per marcatura eIDAS / blockchain</CardTitle>
            <CardDescription>File: <span className="font-mono">{lastExport.filename}</span> · {fmt(lastExport.at)}. Sottoponi a marca temporale qualificata <b>questa impronta</b> (è anche nel file <span className="font-mono">.sha256</span> scaricato).</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-xs">
            <div className="flex items-center gap-2 flex-wrap"><span className="text-muted-foreground w-36">SHA-256 documento</span><code className="font-mono break-all" data-testid="eula-export-sha256">{lastExport.digest}</code><Button size="sm" variant="ghost" className="h-7" onClick={() => copy(lastExport.digest, 'SHA-256 copiato')} data-testid="eula-export-copy-sha"><Copy className="h-3 w-3" /></Button></div>
            {lastExport.dataset && <div className="flex items-center gap-2 flex-wrap"><span className="text-muted-foreground w-36">SHA-256 dataset</span><code className="font-mono break-all">{lastExport.dataset}</code></div>}
            {lastExport.head && <div className="flex items-center gap-2 flex-wrap"><span className="text-muted-foreground w-36">Head hash audit</span><code className="font-mono break-all">{lastExport.head}</code></div>}
            {lastExport.original && <div className="flex items-center gap-2 flex-wrap"><span className="text-muted-foreground w-36">SHA-256 originale marcato</span><code className="font-mono break-all" data-testid="eula-export-original-sha">{lastExport.original}</code></div>}
            <div className="flex items-center gap-2 flex-wrap pt-1"><span className="text-muted-foreground w-36">Marca temporale</span>
              {lastExport.timestamp?.sealed
                ? <Badge className="rounded-full bg-primary/10 text-primary hover:bg-primary/10" data-testid="eula-export-sealed-badge"><ShieldCheck className="h-3 w-3 mr-1" /> Sigillato · {fmt(lastExport.timestamp.gen_time)} · {lastExport.timestamp.signer_cn} · serie {lastExport.timestamp.serial_number}</Badge>
                : <>
                    <TimestampButton sha256={lastExport.digest} filename={lastExport.filename} kind={`export_${lastExport.format.toLowerCase()}`} existing={lastExport.timestamp} onDone={(ts) => { setLastExport((le) => ({ ...le, timestamp: ts })); onExported?.(); }} />
                    {lastExport.timestamp && lastExport.format === 'PDF' && <SealedDownloadButton sha256={lastExport.digest} filename={lastExport.filename} />}
                  </>}
            </div>
            {lastExport.timestamp?.sealed && <p className="text-[11px] text-muted-foreground pt-1">Il PDF sigillato contiene: le pagine originali, una pagina finale con tutti i dati della marca e, come allegati, il PDF originale marcato e la ricevuta <span className="font-mono">.tsr</span>. Da marcare/conservare: l'impronta <b>originale</b> (nel file <span className="font-mono">.sha256</span> scaricato).</p>}
          </CardContent>
        </Card>
      )}

      {/* Tabella */}
      <Card className="rounded-2xl overflow-hidden">
        <AcceptancesTableBody error={error} loading={loading} data={data} onRetry={load} onCopy={copy} />
      </Card>
    </div>
  );
};
